/**
 * Notifications API helpers.
 *
 * `getNotificationLabel` / `getNotificationLink` describe a notification for
 * the UI. Both the legacy prod notification_type_id values (e.g. `new_aura`,
 * `like_post`, `new_follower`) that were carried over during the March 2026
 * prod import and the current app's type set (`post_mention`, `follow`,
 * `follow_request_accepted`, etc.)
 * are supported here so neither surface ever falls back to "user notified you".
 */

const LEGACY_TYPES_POST = ['like_post', 'new_comment', 'new_reply', 'new_aura', 're_sphere'];
/** Includes legacy `new_follower`; `follow_request_accepted` is stored for new accepts too. */
const LEGACY_TYPES_FOLLOW = ['new_follower'];

export function getNotificationLabel(n) {
  const name = n.actor_displayname || n.actor_username || 'Someone';
  switch (n.notification_type_id) {
    // Current app types
    case 'club_invite':
      return `${name} invited you to ${n.club_name || 'a club'}`;
    case 'event_invite':
      return `${name} invited you to ${n.event_name || 'an event'}`;
    case 'club_join_request':
      return `${name} requested to join ${n.club_name || 'your club'}`;
    case 'follow_request':
      return `${name} requested to follow you`;
    case 'follow_request_accepted':
      return `${name} accepted your follow request`;
    case 'follow':
      return `${name} started following you`;
    case 'post_mention':
      return `${name} mentioned you in a post`;
    case 'comment_mention':
      return `${name} mentioned you in a comment`;
    case 'dm_message':
      return `${name} sent you a message`;
    case 'group_message':
      return `${name} sent a message to your group`;
    case 'club_message':
      return `${name} sent a message${n.club_name ? ` in ${n.club_name}` : ''}`;
    // Legacy prod types (carried over by the 2026-03-03 import)
    case 'new_follower':
      return `${name} started following you`;
    case 'new_aura':
      return `${name} sent you aura`;
    case 'new_comment':
      return `${name} commented on your post`;
    case 'new_reply':
      return `${name} replied to your comment`;
    case 'like_post':
      return `${name} liked your post`;
    case 'like_comment':
      return `${name} liked your comment`;
    case 'like_reply':
      return `${name} liked your reply`;
    case 're_sphere':
      return `${name} re-shared your post`;
    case 'message_reaction':
      return `${name} reacted to your message`;
    default:
      return `${name} notified you`;
  }
}

export function getNotificationLink(n) {
  const isPostLink =
    n.notification_type_id === 'post_mention' ||
    n.notification_type_id === 'comment_mention' ||
    LEGACY_TYPES_POST.includes(n.notification_type_id);
  if (isPostLink && n.link_post_id) {
    return `/posts/${n.link_post_id}`;
  }
  const isFollowLink =
    n.notification_type_id === 'follow' ||
    n.notification_type_id === 'follow_request' ||
    n.notification_type_id === 'follow_request_accepted' ||
    LEGACY_TYPES_FOLLOW.includes(n.notification_type_id);
  if (isFollowLink && n.actor_username) {
    return `/profile/${n.actor_username}`;
  }
  if (n.notification_type_id === 'club_join_request' && n.club_id) {
    return `/clubs/${n.club_id}`;
  }
  if (n.notification_type_id === 'club_invite' && n.club_id) {
    return `/clubs/${n.club_id}`;
  }
  if (n.notification_type_id === 'event_invite' && n.event_id) {
    return `/events/${n.event_id}`;
  }
  if (n.notification_type_id === 'dm_message' && n.conversation_id) {
    return `/messages/${n.conversation_id}`;
  }
  if (n.notification_type_id === 'group_message' && n.group_conversation_id) {
    return `/messages/group/${n.group_conversation_id}`;
  }
  if (n.notification_type_id === 'club_message' && n.club_id) {
    return `/clubs/${n.club_id}?tab=chat`;
  }
  return null;
}

/** Unread notifications for RightSidebar */
export async function getUnreadNotifications(limit = 3) {
  const res = await fetch(`/api/notifications/unread?limit=${limit}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch notifications');
  const data = await res.json();
  return data.notifications || [];
}

/** All notifications for Notifications route */
export async function getNotifications(limit = 50, offset = 0) {
  const res = await fetch(
    `/api/notifications?limit=${limit}&offset=${offset}`,
    { credentials: 'include' }
  );
  if (!res.ok) throw new Error('Failed to fetch notifications');
  return res.json();
}

/** Mark all notifications as read (legacy) */
export async function markNotificationsRead() {
  const res = await fetch('/api/notifications/mark-read', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to mark as read');
  return res.json();
}

/** Unread count for badges */
export async function getUnreadNotificationCount() {
  const res = await fetch('/api/notifications/unread-count', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch unread count');
  const data = await res.json();
  return typeof data.count === 'number' ? data.count : 0;
}

/** Mark a single notification read */
export async function markNotificationReadOne(notificationId) {
  const res = await fetch(`/api/notifications/${notificationId}/mark-read`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to mark notification read');
  return res.json();
}

/** Remove a notification for the current user */
export async function deleteNotification(notificationId) {
  const res = await fetch(`/api/notifications/${notificationId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to remove notification');
  }
  return res.json();
}

export async function acceptFollowRequest(followerId) {
  const res = await fetch(`/api/users/follow-requests/${followerId}/accept`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to accept');
  }
  return res.json();
}

export async function declineFollowRequest(followerId) {
  const res = await fetch(`/api/users/follow-requests/${followerId}/decline`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to decline');
  return res.json();
}

export async function acceptClubInvite(inviteId) {
  const res = await fetch(`/api/club-invites/${inviteId}/accept`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to accept invite');
  }
  return res.json();
}

export async function declineClubInvite(inviteId) {
  const res = await fetch(`/api/club-invites/${inviteId}/decline`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to decline invite');
  }
  return res.json();
}

export async function acceptEventInvite(inviteId) {
  const res = await fetch(`/api/event-invites/${inviteId}/accept`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to accept invite');
  }
  return res.json();
}

export async function declineEventInvite(inviteId) {
  const res = await fetch(`/api/event-invites/${inviteId}/decline`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to decline invite');
  }
  return res.json();
}
